import {
  PLANNER_RESPONSE_LABELS,
  plannerResponseOf,
} from './forecast-queue-format'
import type {
  AwaitingNextMeetingRow,
  ForecastQueueRow,
  PlannerResponseState,
} from '@/types/backofficeAnalytics.types'

/**
 * The Forecasts console's working lists, grouped (sc-1212).
 *
 * ## Why this module exists
 *
 * The page used to split its three queue tabs on *has a date been promised, and
 * has it passed* — which is why a case with nothing on the calendar could land
 * under "Scheduled". Tyler, 2026-10-20: *"this one seems mis categorized cause
 * if its missing meetings isnt that No Date?"* It was not miscategorised; the
 * axis simply did not express the thing a reader was looking for.
 *
 * **The axis is now "is a meeting booked".** `Scheduled` holds every open case
 * with a booked delivery meeting; `Unscheduled Work` holds every case without
 * one, delivered or not. Each tab is then grouped by what to do next, and the
 * groups are what the chips above the table filter to.
 *
 * ## Why the grouping is here and not on the API
 *
 * Tyler's call: *"The Grouping is in the UI not API."* There is a stronger
 * reason to keep it here as well. `ForecastQueueGroup`'s own doc comment says
 * *"never re-bucket a row by its own `group` client-side"* — and nothing below
 * does: these functions bucket on whether a meeting exists, which the server
 * does not express at all. **`group` on the wire cannot change**, because
 * `case-mute.ts` lapses a park whenever a case's situation group changes and
 * `case_queue_snapshots` stores it, so repointing the server's grouping would
 * lapse every park and put a step change in the history. Leaving `group`
 * untouched and grouping for display here avoids both.
 *
 * ## The shape every group function shares
 *
 * **First match wins, and the order is the order a reader should work them.**
 * Every row lands in exactly one group of its tab, so the chip counts sum to
 * the list length and no row is shown twice. The ordered arrays are the single
 * source for chip order, group headings and the Reference tab's bullets — the
 * same "one array, many renderings" rule `LIST_TABS` already follows.
 */

/** A group's key, label and the sentence that explains it. */
export interface ForecastGroup<TKey extends string> {
  key: TKey
  /** Chip text and group heading. Short: it has to fit a chip beside a count. */
  label: string
  /** One sentence under the heading, and the Reference tab's bullet. */
  description: string
}

/**
 * The one predicate the two tabs split on.
 *
 * Named once rather than spelled `row.deadlineAt !== null` at each site,
 * because it is now load-bearing for which TAB a row appears in, not just a
 * null check. `deadlineAt` is the chosen delivery meeting — see the field's own
 * doc comment for how one is picked from a case's diary.
 */
export function isBooked(row: Pick<ForecastQueueRow, 'deadlineAt'>): boolean {
  return row.deadlineAt !== null
}

export type ScheduledGroupKey =
  | 'OVERDUE'
  | 'PAST_SAFE_START'
  | 'START_NOW'
  | 'ON_SCHEDULE'

/**
 * `Scheduled` — a meeting is booked, so the job is to build the plan in time.
 *
 * `START_NOW` reads 0 on a normal board and its chip still renders. That is
 * deliberate: the band went to zero when `bandFor` started grading a started
 * case on whether it will land rather than on whether a date went by, so "no
 * case is in that state today" is a true and useful thing for the page to say.
 * Hiding the chip would make the absence look like a missing feature.
 */
export const SCHEDULED_GROUPS: ReadonlyArray<ForecastGroup<ScheduledGroupKey>> =
  [
    {
      key: 'OVERDUE',
      label: 'Overdue',
      description:
        'Past the date the plan had to be ready. The meeting is still coming.',
    },
    {
      key: 'PAST_SAFE_START',
      label: 'Past the safe start',
      description:
        'Work should already have begun for this to land on past form.',
    },
    {
      key: 'START_NOW',
      label: 'Start now',
      description: 'The safe start falls inside the next few days.',
    },
    {
      key: 'ON_SCHEDULE',
      label: 'On schedule',
      description: 'Booked, with room left before work has to start.',
    },
  ]

/**
 * Which `Scheduled` group a row belongs to.
 *
 * Overdue is tested first and on the DATE rather than the band, because the two
 * answer different questions: the band grades whether the case can still land,
 * and a case can be past its required-ready date while its band reads
 * `SCHEDULED` (the meeting is weeks out and the work has started). A reader
 * scanning for what to clear wants the missed date first.
 *
 * Callers must pass a row where {@link isBooked} is true; an unbooked row has
 * no safe start and no deadline, so every test below would fall through to
 * `ON_SCHEDULE` and quietly file it as healthy. Asserted in the spec rather
 * than guarded at runtime, since the caller is the tab split.
 */
export function scheduledGroupFor(
  row: Pick<ForecastQueueRow, 'requiredReadyBy' | 'band'>,
  now: Date,
): ScheduledGroupKey {
  if (row.requiredReadyBy && new Date(row.requiredReadyBy) < now) {
    return 'OVERDUE'
  }
  if (row.band === 'AT_RISK') return 'PAST_SAFE_START'
  if (row.band === 'START_NOW') return 'START_NOW'
  return 'ON_SCHEDULE'
}

export type UnscheduledGroupKey =
  | 'OVER_BUDGET'
  | 'DELIVERED_NOTHING_NEXT'
  | 'READY_TO_BOOK'
  | 'WAITING_ON_DG'

/**
 * `Unscheduled Work` — nothing is on the calendar, so the job is to book one.
 *
 * **One row shape per group, which is why the order is what it is.** Tyler:
 * *"if Scheduled has work due as well and each has different rows id get
 * confused."* `DELIVERED_NOTHING_NEXT` holds `AwaitingNextMeetingRow`s, which
 * carry `deliveredAt` / `submitWindow` / `closeOut` and none of the queue's
 * budget or projection fields; the other three hold `ForecastQueueRow`s. Giving
 * each group its own table keeps that difference invisible to a reader, who
 * only ever sees one group's columns at a time.
 *
 * Ordered by who has to act and how late they are:
 *
 * 1. **Over the budget** — the firm missed its own promise with nothing booked.
 *    Three rows on production 2026-10-20, and the worst of them (98 days old,
 *    36 business days over) is invisible today: it has no promised date, so it
 *    is not in Urgent, and the undated list sorts oldest-first and buries it at
 *    rank 20 of 97.
 * 2. **Delivered, nothing next** — a client has the plan and no next meeting.
 * 3. **Ready to book** — data gathering is approved, the clock is running.
 * 4. **Waiting on data gathering** — the client has not finished. The majority
 *    (74 of 95 unbooked open rows), and correctly quiet: nothing here is the
 *    specialist's to fix.
 */
export const UNSCHEDULED_GROUPS: ReadonlyArray<
  ForecastGroup<UnscheduledGroupKey>
> = [
  {
    key: 'OVER_BUDGET',
    label: 'Over the budget',
    description:
      'The turnaround budget is spent and no meeting has been booked.',
  },
  {
    key: 'DELIVERED_NOTHING_NEXT',
    label: 'Delivered — nothing next',
    description:
      'The plan went out and there is no next meeting on the calendar.',
  },
  {
    key: 'READY_TO_BOOK',
    label: 'Ready to book',
    description:
      'Data gathering is approved and the clock is running, with nothing booked.',
  },
  {
    key: 'WAITING_ON_DG',
    label: 'Waiting on data gathering',
    description:
      'No approved data-gathering form yet, so the work cannot start and no meeting is owed.',
  },
]

/**
 * Which `Unscheduled Work` group an open queue row belongs to.
 *
 * `DELIVERED_NOTHING_NEXT` is NOT returned here — that group is the Ready-stage
 * population, a different row type entirely, and it is added to the tab
 * alongside these rather than derived from them. A queue row that has been
 * delivered and has nothing booked carries
 * `unscheduledReason === 'DELIVERED_NOTHING_BOOKED'` and still belongs with the
 * open rows, because it is sitting in a work stage with work to do.
 *
 * Over-budget is tested before waiting-on-DG so a case whose clock ran out
 * cannot hide behind a missing approval — though the two are mutually exclusive
 * in practice, since a case with no approval has no clock to spend.
 */
export function unscheduledGroupFor(
  row: Pick<
    ForecastQueueRow,
    'budgetBusinessDaysLeft' | 'waitingOnDataGathering'
  >,
): Exclude<UnscheduledGroupKey, 'DELIVERED_NOTHING_NEXT'> {
  if (row.budgetBusinessDaysLeft !== null && row.budgetBusinessDaysLeft <= 0) {
    return 'OVER_BUDGET'
  }
  if (row.waitingOnDataGathering) return 'WAITING_ON_DG'
  return 'READY_TO_BOOK'
}

export type PlannerGroupKey = PlannerResponseState

/**
 * `Awaiting planner` -- delivered cases, graded on how long the PLANNER has had
 * the feedback from a held meeting.
 *
 * The tab used to grade a delivered case by a submit window counted BACKWARDS
 * from the next booked meeting, which pointed at the specialist: Tyler, on a
 * real row, *"Jacob performed the plan for the presentation on Oct 7th, but the
 * way this reads he needs to get the work done yet by Oct 10."* The firm's
 * expectation runs forward: the meeting happens, the client gives feedback, and
 * the planner moves the ticket to Revisions Needed or Alternative Scenarios
 * Needed within a few business days. The labels come from
 * `PLANNER_RESPONSE_LABELS`, shared with the Status column.
 *
 * Order is the order a reader works them, and the counts on today's book are
 * 53 / 32 / 37 / 717.
 */
const PLANNER_GROUP_DESCRIPTIONS: Record<PlannerGroupKey, string> = {
  JUST_HELD: 'Inside the grace period after the meeting. Nothing to do yet.',
  CHASE: 'Past the grace period: worth a nudge to the planner.',
  LATE: 'The planner is holding the case up.',
  STALLED:
    'Over a fortnight since the meeting. Either work is owed or the ticket should be closed.',
}

export const PLANNER_GROUPS: ReadonlyArray<ForecastGroup<PlannerGroupKey>> = (
  ['JUST_HELD', 'CHASE', 'LATE', 'STALLED'] as const
).map((key) => ({
  key,
  label: PLANNER_RESPONSE_LABELS[key],
  description: PLANNER_GROUP_DESCRIPTIONS[key],
}))

/**
 * Which `Awaiting planner` group a delivered row belongs to, or null.
 *
 * **Null is explicit, not a default bucket.** A null `plannerResponse` means no
 * clock to read (no meeting held since delivery, or a request already raised);
 * an absent one is an API that predates the field. Filing either under a state
 * would invent a verdict, so such a row is in no group and the caller decides
 * where to show it.
 */
export function plannerGroupFor(
  row: Pick<AwaitingNextMeetingRow, 'plannerResponse'>,
): PlannerGroupKey | null {
  return plannerResponseOf(row)
}

/**
 * How many rows fall in each group — the numbers on the chips.
 *
 * **Every key is present, including the zero ones.** A chip reading 0 says
 * "nothing is in this state", which is information; a missing chip reads as a
 * missing feature. Same reason `queueTabCounts` renders `null` rather than
 * omitting a tab while the query is pending.
 *
 * Call this on the rows that have passed every filter EXCEPT the chip
 * selection, so a count does not change when the reader selects it — the rule
 * `CleaningKindFilterChips` already follows, and the one that makes the numbers
 * usable as a summary of the list.
 */
export function groupCounts<TKey extends string, TRow>(
  rows: ReadonlyArray<TRow>,
  groups: ReadonlyArray<ForecastGroup<TKey>>,
  groupFor: (row: TRow) => TKey | null,
): Record<TKey, number> {
  const counts = Object.fromEntries(
    groups.map((group) => [group.key, 0]),
  ) as Record<TKey, number>
  for (const row of rows) {
    const key = groupFor(row)
    if (key !== null) counts[key] += 1
  }
  return counts
}
